import {
  ATTACHMENT_MAX_BYTES,
  ATTACHMENTS_PER_MESSAGE,
  assistantAttachmentSchema,
  type AssistantAttachment,
} from '@secretario/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiBinary, ApiError, apiFetch } from './api/http.js';

/**
 * Anexos da conversa com o Secretário, do lado do app: a foto é reduzida no aparelho
 * (1.600 px, JPEG) e cada arquivo sobe assim que é escolhido, enquanto a pessoa escreve
 * ou fala. Na hora de enviar, a mensagem espera só o que ainda está subindo.
 */

export type AttachmentKind = AssistantAttachment['kind'];

export type PendingAttachment = {
  key: string;
  name: string;
  kind: AttachmentKind;
  /** Miniatura local (só fotos que o navegador desenha). */
  preview: string | null;
  state: 'uploading' | 'ready' | 'failed';
  id?: string;
  error?: string;
};

const PHOTO_EDGE = 1600;
const IMAGE = /^image\/(jpeg|png|webp|heic|heif)$/;
const TEXT_EXTENSIONS = /\.(txt|csv|md|docx|xlsx|pptx)$/i;

export function kindOfFile(file: File): AttachmentKind | null {
  if (IMAGE.test(file.type) || /\.(heic|heif)$/i.test(file.name)) return 'image';
  if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) return 'pdf';
  if (
    TEXT_EXTENSIONS.test(file.name) ||
    file.type.startsWith('text/') ||
    file.type.includes('officedocument')
  )
    return 'text';
  return null;
}

/** Foto grande vira JPEG com até 1.600 px: sobe mais rápido e é o que o modelo lê. */
async function shrinkPhoto(file: File): Promise<Blob> {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, PHOTO_EDGE / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size < 900 * 1024) {
      bitmap.close();
      return file;
    }
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const smaller = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.85),
    );
    return smaller && smaller.size < file.size ? smaller : file;
  } catch {
    return file;
  }
}

function encodeMeta(meta: { name: string; mime: string }): string {
  const bytes = new TextEncoder().encode(JSON.stringify(meta));
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export async function uploadAttachment(file: File): Promise<AssistantAttachment> {
  const body = await shrinkPhoto(file);
  const response = await apiBinary('/v1/assistant/attachments', {
    method: 'POST',
    body,
    headers: { 'x-attach-meta': encodeMeta({ name: file.name || 'anexo', mime: file.type }) },
  });
  return assistantAttachmentSchema.parse(await response.json());
}

/** Conversa ao vivo: a foto descrita (ou o texto do arquivo) para entrar na conversa. */
export function describeAttachment(
  id: string,
): Promise<{ name: string; kind: AttachmentKind; text: string }> {
  return apiFetch(`/v1/assistant/attachments/${encodeURIComponent(id)}/describe`, {
    method: 'POST',
    body: {},
  });
}

/** Miniaturas do histórico: o arquivo vem com o token, vira um endereço local e fica guardado. */
const thumbs = new Map<string, Promise<string | null>>();
export function attachmentUrl(id: string): Promise<string | null> {
  let found = thumbs.get(id);
  if (!found) {
    found = apiBinary(`/v1/assistant/attachments/${encodeURIComponent(id)}/content`)
      .then((response) => response.blob())
      .then((blob) => URL.createObjectURL(blob))
      .catch(() => null);
    thumbs.set(id, found);
  }
  return found;
}

/** Guarda a miniatura local de um anexo recém-enviado, para o balão não piscar. */
export function rememberPreview(id: string, preview: string | null): void {
  if (preview && !thumbs.has(id)) thumbs.set(id, Promise.resolve(preview));
}

let counter = 0;

export function useAttachments() {
  const [items, setItems] = useState<PendingAttachment[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  // Cada envio guarda o próprio resultado (id ou null): a bandeja some na hora de mandar,
  // e a mensagem ainda espera o que estava subindo.
  const uploads = useRef(new Map<string, Promise<string | null>>());
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const patch = (key: string, next: Partial<PendingAttachment>) =>
    setItems((current) => current.map((item) => (item.key === key ? { ...item, ...next } : item)));

  const add = useCallback((files: File[]) => {
    setNotice(null);
    const room = ATTACHMENTS_PER_MESSAGE - itemsRef.current.length;
    if (files.length > room) setNotice(`Cabem ${ATTACHMENTS_PER_MESSAGE} anexos por mensagem.`);
    for (const file of files.slice(0, Math.max(0, room))) {
      const kind = kindOfFile(file);
      if (!kind) {
        setNotice('Esse tipo eu ainda não leio. Manda em foto, PDF, Word ou Excel.');
        continue;
      }
      if (file.size > ATTACHMENT_MAX_BYTES) {
        setNotice('Esse arquivo passa de 20 MB. Consegue mandar um menor?');
        continue;
      }
      counter += 1;
      const key = `anexo-${counter}`;
      const preview =
        kind === 'image' && /^image\/(jpeg|png|webp)$/.test(file.type)
          ? URL.createObjectURL(file)
          : null;
      setItems((current) => [
        ...current,
        { key, name: file.name || 'anexo', kind, preview, state: 'uploading' },
      ]);
      const job = uploadAttachment(file)
        .then((saved) => {
          rememberPreview(saved.id, preview);
          patch(key, { state: 'ready', id: saved.id });
          return saved.id;
        })
        .catch((error: unknown) => {
          patch(key, {
            state: 'failed',
            error:
              error instanceof ApiError && error.message
                ? error.message
                : 'Não consegui mandar esse arquivo. Tira e tenta de novo.',
          });
          return null;
        });
      uploads.current.set(key, job);
    }
  }, []);

  const remove = useCallback((key: string) => {
    uploads.current.delete(key);
    setItems((current) => current.filter((item) => item.key !== key));
  }, []);

  /** Depois de enviar: some a bandeja (as miniaturas seguem no balão). */
  const clear = useCallback(() => {
    setItems([]);
    setNotice(null);
  }, []);

  /** Espera os envios destes anexos e devolve os ids prontos, na ordem escolhida. */
  const settle = useCallback(async (keys: string[]): Promise<{ ids: string[]; failed: number }> => {
    const results = await Promise.all(keys.map((key) => uploads.current.get(key) ?? null));
    for (const key of keys) uploads.current.delete(key);
    const ids = results.filter((id): id is string => typeof id === 'string');
    return { ids, failed: keys.length - ids.length };
  }, []);

  useEffect(
    () => () => {
      // Miniaturas que não viraram balão: libera a memória.
      for (const item of itemsRef.current) {
        if (item.preview && item.state !== 'ready') URL.revokeObjectURL(item.preview);
      }
    },
    [],
  );

  return {
    items,
    notice,
    add,
    remove,
    clear,
    settle,
    busy: items.some((item) => item.state === 'uploading'),
    count: items.length,
  };
}
